import React from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import AskAsperIcon from '../../../atoms/Icon/AskAsperIcon';

export interface StandaloneSummaryCardProps {
    /** Summary text content — supports **bold** markers and \n for line breaks. Each line is rendered as a bullet point. Can be a string or an array of sentences. */
    summary: string | string[];
    /** ISO 8601 or pre-formatted timestamp displayed centered above AskAsper branding (e.g. "Jan 13, 2026 at 10:42 AM") */
    timestamp?: string;
    /** Follow-up question strings displayed as pill buttons */
    followUpQuestions?: string[];
    /** Called when a follow-up question is clicked — populates the input bar */
    onFollowUpClick?: (question: string) => void;
    'data-test'?: string;
    'data-test-id'?: string;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Format ISO 8601 timestamp to "Jan 13, 2026 at 10:42 AM" (matches Figma design) */
const formatTimestamp = (isoOrFormatted: string): string => {
    try {
        if (/[A-Za-z]{3}\s+\d{1,2},\s+\d{4}\s+at\s+\d{1,2}:\d{2}\s+(?:AM|PM)/i.test(isoOrFormatted)) {
            return isoOrFormatted;
        }
        const date = new Date(isoOrFormatted);
        if (Number.isNaN(date.getTime())) return isoOrFormatted;
        const month = MONTHS[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        const minStr = String(minutes).padStart(2, '0');
        return `${month} ${day}, ${year} at ${hours}:${minStr} ${ampm}`;
    } catch {
        return isoOrFormatted;
    }
};

/**
 * Renders a line of text, converting **bold** markers into <strong> elements.
 */
const renderBoldText = (text: string): React.ReactNode => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            const boldText = part.slice(2, -2);
            return (
                <strong key={index} className="font-semibold">
                    {boldText}
                </strong>
            );
        }
        return <span key={index}>{part}</span>;
    });
};

/**
 * StandaloneSummaryCard — Shown in the standalone agentic layout's left panel.
 *
 * Displays:
 * - AskAsper branding (icon + label)
 * - Summary bullet points (supports **bold** markdown)
 * - "What would you like to do next?" follow-up question pills
 */
const StandaloneSummaryCard: React.FC<StandaloneSummaryCardProps> = ({
    summary,
    timestamp,
    followUpQuestions = [],
    onFollowUpClick,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();

    // Default to current time if no timestamp provided
    const displayTimestamp = formatTimestamp(timestamp ?? new Date().toISOString());

    // Handle both string and array formats
    const lines = Array.isArray(summary)
        ? summary.filter((l) => l.trim().length > 0)
        : summary
              .split('\n')
              .map((l) => l.trim())
              .filter((l) => l.length > 0);

    return (
        <ConfigProvider theme={theme}>
            <div className="space-y-5" data-test={dataTest} data-test-id={dataTestId} data-standalone-summary-card>
                {/* Timestamp */}
                <p className="font-['Open_Sans'] text-sm leading-[16px] tracking-[0.3px] text-[#9f9fa9] text-center w-full">
                    {displayTimestamp}
                </p>

                {/* AskAsper Branding */}
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center">
                        <div className="scale-[0.55] origin-center">
                            <AskAsperIcon size={48} />
                        </div>
                    </div>
                    <span className="font-['Open_Sans'] font-semibold text-[16px] leading-[20px] text-[rgba(0,0,0,0.88)]">
                        AskAsper
                    </span>
                </div>

                {/* Summary Bullets */}
                {lines.length > 0 && (
                    <ul className="list-disc pl-5 space-y-2 m-0">
                        {lines.map((line, index) => (
                            <li
                                key={index}
                                className="font-['Open_Sans'] text-[16px] leading-[26px] text-[rgba(0,0,0,0.88)]"
                            >
                                {renderBoldText(line)}
                            </li>
                        ))}
                    </ul>
                )}

                {/* Follow-up Questions */}
                {followUpQuestions.length > 0 && (
                    <div className="space-y-3 pt-2">
                        <p className="font-['Open_Sans'] font-semibold text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] m-0">
                            What would you like to do next?
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {followUpQuestions.map((question, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => onFollowUpClick?.(question)}
                                    className="bg-[#F0E8FA] text-[#6B46C1] font-['Open_Sans'] font-normal text-[16px] leading-[20px] px-4 py-2 rounded-[27px] border border-transparent hover:bg-[#E9D5FF] transition-colors cursor-pointer text-left"
                                    data-test-id={`${dataTestId}-followup-${index}`}
                                >
                                    {question}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default StandaloneSummaryCard;
